import { Elements, PaymentElement } from "@stripe/react-stripe-js"
import { StripePaymentElementChangeEvent } from "@stripe/stripe-js"
import { useSuspenseQuery } from "@tanstack/react-query"
import { computedColorValues } from "source/shared/colors"
import { useDonationFormContext } from "../hooks/useDonationFormContext"
import { fetchStripeCSCSecretQuery, fetchStripeObjectQuery } from "../queries"
import { Currency, Frequency, StripePaymentMethod, Total } from "../types"
import { shouldSavePaymentMethod } from "../utils/paymentMethods"

// TODO: Replace with min/max obtained from donation form config
const STRIPE_PAYMENT_ELEMENT_MINIMUM_AMOUNT_CENTS = 50
const STRIPE_PAYMENT_ELEMENT_MAXIMUM_AMOUNT_CENTS = 99999999

const { brand, tint0, tint2, tint3, tint9, tint10 } = computedColorValues

const stripeElementsAppearance = {
  variables: {
    borderRadius: "8px",
    colorBackground: tint9,
    colorPrimary: brand,
    colorTextSecondary: tint2,
    fontSizeSm: "14px",
    spacingGridRow: "0.5rem",
  },
  rules: {
    ".AccordionItem": {
      border: "unset",
      boxShadow: "unset",
      padding: "1rem",
      fontSize: "var(--fontSizeBase)",
      fontWeight: "normal",
      color: tint0,
    },
    ".AccordionItem--selected": {
      color: tint0,
      fontWeight: "500",
    },
    ".Action": {
      fontWeight: "500",
    },
    ".Label": {
      marginBottom: "0.5rem",
    },
    ".Input": {
      backgroundColor: tint10,
      borderColor: "transparent",
      boxShadow: "unset",
      paddingTop: "10px", // adjust to ~40px total height
      paddingBottom: "10px", // adjust to ~40px total height
      borderRadius: "1.5rem",
    },
    ".PickerItem": {
      backgroundColor: tint10,
      borderColor: "transparent",
      boxShadow: "unset",
      fontSize: "var(--fontSizeSm)",
      padding: "0.5rem",
    },
    ".PickerItem:hover": {
      backgroundColor: tint10,
      borderColor: tint3,
    },
    ".PickerItem--selected": {
      boxShadow: "unset",
    },
    ".PickerItem--selected:hover": {
      boxShadow: "unset",
    },
    ".RadioIconInner--checked": {
      fill: tint10,
    },
    ".RadioIconOuter": {
      fill: tint10,
      strokeWidth: "5",
      stroke: tint3,
    },
    ".RadioIconOuter--checked": {
      stroke: brand,
      strokeWidth: "30",
    },
    ".TabIcon": {
      fill: tint2,
    },
  },
}

type StripeElementsProviderProps = {
  amountCents: Total
  currency: Currency
  frequency: Frequency
  stripePaymentMethod?: StripePaymentMethod
  savePaymentMethodChecked: boolean
  children: NonNullable<React.ReactNode>
}

function StripeElementsProvider({
  amountCents,
  currency,
  frequency,
  stripePaymentMethod,
  savePaymentMethodChecked,
  children,
}: StripeElementsProviderProps) {
  const { config } = useDonationFormContext()

  const { data: stripeObject } = useSuspenseQuery(
    fetchStripeObjectQuery(config),
  )

  const { data: stripeCustomerSessionClientSecret } = useSuspenseQuery(
    fetchStripeCSCSecretQuery(),
  )

  // TODO: Obtain this via some kind of validation/util function
  const validAmount =
    amountCents >= STRIPE_PAYMENT_ELEMENT_MINIMUM_AMOUNT_CENTS &&
    amountCents <= STRIPE_PAYMENT_ELEMENT_MAXIMUM_AMOUNT_CENTS

  const savePaymentMethod = shouldSavePaymentMethod({
    frequency,
    stripePaymentMethod,
    savePaymentMethodChecked,
  })

  const elementsAmount = () => {
    if (savePaymentMethod) {
      return
    }
    return validAmount
      ? amountCents
      : STRIPE_PAYMENT_ELEMENT_MINIMUM_AMOUNT_CENTS
  }

  if (stripeObject && stripeCustomerSessionClientSecret) {
    return (
      <Elements
        stripe={stripeObject}
        options={{
          mode: savePaymentMethod ? "setup" : "payment",
          setupFutureUsage: savePaymentMethod ? "off_session" : null,
          amount: elementsAmount(),
          currency,
          customerSessionClientSecret: stripeCustomerSessionClientSecret,
          appearance: stripeElementsAppearance,
        }}
      >
        {children}
      </Elements>
    )
  } else {
    return null
  }
}

type ConditionalStripeElementsProviderProps = {
  condition: boolean
  amountCents: Total
  currency: Currency
  frequency: Frequency
  stripePaymentMethod?: StripePaymentMethod
  savePaymentMethodChecked: boolean
  children: NonNullable<React.ReactNode>
}

export function ConditionalStripeElementsProvider({
  condition,
  amountCents,
  currency,
  frequency,
  stripePaymentMethod,
  savePaymentMethodChecked,
  children,
}: ConditionalStripeElementsProviderProps) {
  if (condition) {
    return (
      <StripeElementsProvider
        {...{
          amountCents,
          currency,
          frequency,
          stripePaymentMethod,
          savePaymentMethodChecked,
        }}
      >
        {children}
      </StripeElementsProvider>
    )
  } else {
    return children
  }
}

export function StripePaymentElement() {
  const { config, dispatch } = useDonationFormContext()

  const handleChange = (event: StripePaymentElementChangeEvent) => {
    dispatch({
      type: "SET_STRIPE_PAYMENT_METHOD",
      stripePaymentMethod: {
        id: event.value.payment_method?.id,
        type: event.value.type,
      },
    })
  }

  return (
    <PaymentElement
      options={{
        layout: {
          type: "accordion",
          defaultCollapsed: false,
          radios: true,
          spacedAccordionItems: true,
        },
        fields: {
          billingDetails: {
            name: "auto",
            email: "never",
            phone: "auto",
            address: "auto",
          },
        },
        defaultValues: {
          billingDetails: { name: config.default_payment_method_name },
        },
        paymentMethodOrder: [
          "apple_pay",
          "us_bank_account",
          "au_becs_debit",
          "card",
        ],
        terms: {
          card: "never",
          usBankAccount: "never",
          auBecsDebit: "never",
        },
        wallets: {
          applePay: "auto",
          googlePay: "never",
        },
      }}
      onChange={handleChange}
    />
  )
}
